import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar, { ASSETS } from '../components/Navbar';
import { ArrowRight, Shield, Users, Calendar, Heart, Sparkles, Star } from 'lucide-react';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#0B0B0B] overflow-hidden">
      <Navbar />

      {/* ========== HERO SECTION - IMAGEN ARRIBA, TEXTO ABAJO ========== */}
      <section className="pt-20">
        {/* Hero Image - Full width, complete image without cropping */}
        <div className="w-full">
          <img 
            src={ASSETS.hero}
            alt="Soul Mate Dates"
            className="w-full h-auto object-contain"
          />
        </div>

        {/* Hero Content - Below the image on black background */}
        <div className="bg-[#0B0B0B] py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            {/* Logo */}
            <div className="animate-cinematic delay-100">
              <img 
                src={ASSETS.logo}
                alt="Soul Mate Dates Logo"
                className="w-28 h-28 md:w-36 md:h-36 mx-auto logo-glow object-contain mb-10"
              />
            </div>

            {/* Tagline */}
            <p className="label-premium mb-6 animate-cinematic delay-200">
              {t('hero_tagline')}
            </p>

            {/* Main headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif text-[#F5F0E6] leading-tight animate-cinematic delay-300">
              {t('hero_title_1')}
            </h1>
            <p className="text-3xl sm:text-4xl md:text-5xl font-serif mt-2 wine-gradient animate-cinematic delay-400">
              {t('hero_title_2')}
            </p>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-[#8A8A8A] mt-8 max-w-2xl mx-auto leading-relaxed animate-cinematic delay-500">
              {t('hero_subtitle')}
            </p>

            {/* CTA Button */}
            <div className="mt-12 animate-cinematic delay-600">
              <Link 
                to={isAuthenticated ? "/dashboard" : "/apply"}
                className="btn-wine inline-flex items-center gap-3"
                data-testid="hero-apply-button"
              >
                {isAuthenticated ? t('hero_cta_dashboard') : t('hero_cta')}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PROBLEM SECTION ========== */}
      <section className="py-24 md:py-32 bg-[#0B0B0B] relative">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="label-premium mb-6">{t('problem_label')}</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F0E6] leading-tight">
            {t('problem_title_1')}
            <br />
            <span className="text-[#8A8A8A]">{t('problem_title_2')}</span>
          </h2>
          <div className="divider-wine mt-12 mb-12 max-w-xs mx-auto" />
          <p className="text-[#8A8A8A] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            {t('problem_text')}
            <br /><br />
            <span className="text-[#8B1A1A]">{t('problem_solution')}</span>
          </p>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="label-premium mb-6">{t('how_label')}</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F0E6]">
              {t('how_title_1')}<br />
              <span className="wine-gradient">{t('how_title_2')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
            {[
              { step: "01", titleKey: 'how_step1_title', descKey: 'how_step1_desc', icon: Shield },
              { step: "02", titleKey: 'how_step2_title', descKey: 'how_step2_desc', icon: Sparkles },
              { step: "03", titleKey: 'how_step3_title', descKey: 'how_step3_desc', icon: Calendar },
              { step: "04", titleKey: 'how_step4_title', descKey: 'how_step4_desc', icon: Heart }
            ].map((item, index) => (
              <div key={index} className="relative group">
                <div className="card-premium p-8 h-full">
                  <div className="text-5xl font-serif wine-gradient opacity-30 mb-6">
                    {item.step}
                  </div>
                  <item.icon className="text-[#8B1A1A] mb-4" size={28} />
                  <h3 className="text-xl font-serif text-[#F5F0E6] mb-3">{t(item.titleKey)}</h3>
                  <p className="text-[#8A8A8A] text-sm leading-relaxed">{t(item.descKey)}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-[1px] bg-gradient-to-r from-[#8B1A1A] to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== EVENTS SECTION ========== */}
      <section className="py-24 md:py-32 bg-[#0B0B0B]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <img 
                src={ASSETS.socialEvent}
                alt="Eventos exclusivos"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <p className="label-premium mb-6">{t('events_label')}</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F0E6] mb-8">
                {t('events_title_1')}<br />
                <span className="wine-gradient">{t('events_title_2')}</span>
              </h2>
              <div className="space-y-6 text-[#8A8A8A]">
                <p className="leading-relaxed">{t('events_text')}</p>
                <ul className="space-y-4">
                  {['events_feature1', 'events_feature2', 'events_feature3', 'events_feature4'].map((key, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Star className="text-[#8B1A1A] flex-shrink-0" size={16} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TABLE EVENT IMAGE ========== */}
      <section className="relative h-[50vh] md:h-[60vh]">
        <img 
          src={ASSETS.tableEvent}
          alt="Mesa elegante"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 pb-20 text-center">
          <p className="text-[#8B1A1A] font-serif text-2xl md:text-3xl italic">
            "{t('events_quote')}"
          </p>
        </div>
      </section>

      {/* ========== FOUNDERS SECTION ========== */}
      <section className="py-24 md:py-32 bg-[#0B0B0B] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <p className="label-premium mb-6">{t('founders_label')}</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F0E6]">
              {t('founders_title')}
            </h2>
            <div className="divider-wine mt-8 mb-4 max-w-xs mx-auto" />
          </div>

          {/* Founders Grid */}
          <div className="space-y-24 md:space-y-32">
            {/* Founder 1 - Jorge */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img 
                  src={ASSETS.jorge}
                  alt={t('jorge_name')}
                  className="w-full max-w-lg mx-auto lg:mx-0"
                />
              </div>
              <div className="text-center lg:text-left">
                <h3 className="font-script text-4xl md:text-5xl wine-gradient mb-2">
                  {t('jorge_name')}
                </h3>
                <p className="label-premium mb-8">{t('jorge_role')}</p>
                <div className="space-y-6 text-[#8A8A8A] leading-relaxed">
                  <p>{t('jorge_bio1')}</p>
                  <p>{t('jorge_bio2')}</p>
                  <p className="text-[#8B1A1A] italic">"{t('jorge_quote')}"</p>
                </div>
              </div>
            </div>

            {/* Founder 2 - Flavia */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 text-center lg:text-right">
                <h3 className="font-script text-4xl md:text-5xl wine-gradient mb-2">
                  {t('flavia_name')}
                </h3>
                <p className="label-premium mb-8">{t('flavia_role')}</p>
                <div className="space-y-6 text-[#8A8A8A] leading-relaxed">
                  <p>{t('flavia_bio1')}</p>
                  <p>{t('flavia_bio2')}</p>
                  <p>{t('flavia_bio3')}</p>
                </div>
              </div>
              <div className="relative order-1 lg:order-2">
                <img 
                  src={ASSETS.flavia}
                  alt={t('flavia_name')}
                  className="w-full max-w-lg mx-auto lg:ml-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA SECTION ========== */}
      <section className="py-24 md:py-32 bg-[#111111] relative">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <p className="label-premium mb-6">{t('cta_label')}</p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-serif text-[#F5F0E6] mb-6">
            {t('cta_title_1')}
          </h2>
          <p className="text-3xl sm:text-4xl md:text-5xl font-serif wine-gradient mb-12">
            {t('cta_title_2')}
          </p>
          <p className="text-[#8A8A8A] text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed">
            {t('cta_text')}
          </p>
          <Link 
            to={isAuthenticated ? "/dashboard" : "/apply"}
            className="btn-wine inline-flex items-center gap-3 text-lg"
            data-testid="cta-apply-button"
          >
            {isAuthenticated ? t('hero_cta_dashboard') : t('hero_cta')}
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-16 bg-[#0B0B0B] border-t border-[#8B1A1A]/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <img 
                src={ASSETS.logo}
                alt="Soul Mate Dates"
                className="h-12 w-auto object-contain"
              />
            </div>
            <div className="flex gap-8 text-sm text-[#8A8A8A]">
              <a href="#" className="hover:text-[#8B1A1A] transition-colors">{t('footer_privacy')}</a>
              <a href="#" className="hover:text-[#8B1A1A] transition-colors">{t('footer_terms')}</a>
              <a href="#" className="hover:text-[#8B1A1A] transition-colors">{t('footer_contact')}</a>
            </div>
            <p className="text-sm text-[#8A8A8A]">
              © 2024 Soul Mate Dates. {t('footer_rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
